import { RightOutlined } from '@ant-design/icons';
import { Button, Flex } from '@revenuemarket/cds';
import { overlays } from '@revenuemarket/overlays';
import { Typography, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { InvoiceMapping } from '~apis/mapping';
import { PermissionGuard } from '~components/Role/PermissionGuard';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';
import { mixpanel } from '~pages/_app';

import SettlementDirectMappingModal from './SettlementDirectMappingModal';
import SettlementListDrawer from './SettlementListDrawer';
import SettlementMappingModal from './SettlementMappingModal';

interface Props extends InvoiceMapping {
  onMappingSuccess: () => void;
}

const MapTransactionToSettlementBtn: React.FC<Props> = ({ onMappingSuccess, ...props }) => {
  const getButtonText = () => {
    if (!props.mappedLabels || props.mappedLabels.length === 0) {
      return `${props.mappedCount}건`;
    }

    // transactionLabelAmountKrw가 가장 큰 항목 찾기
    const maxAmountItem = props.mappedLabels.reduce((max, current) => {
      const maxAmount = max.transactionLabelAmountKrw || 0;
      const currentAmount = current.transactionLabelAmountKrw || 0;
      return currentAmount > maxAmount ? current : max;
    });

    if (
      !maxAmountItem.transactionAt ||
      (!maxAmountItem.transactionDescription && !maxAmountItem.transactionName) ||
      !maxAmountItem.transactionLabelAmountKrw
    ) {
      return `${props.mappedCount}건`;
    }

    const date = dayjs(maxAmountItem.transactionAt).format('M/D');
    const description = maxAmountItem.transactionDescription || maxAmountItem.transactionName || '';
    const remainingCount = props.mappedCount - 1;

    if (remainingCount === 0) {
      return `${date} ${description}`;
    }

    return `${date} ${description} 외 ${remainingCount}건`;
  };

  const getTooltipContent = () => {
    if (!props.mappedLabels || props.mappedLabels.length === 0) {
      return `${props.mappedCount}건`;
    }

    const labels = props.mappedLabels
      .map((label) => {
        if (
          !label.transactionAt ||
          (!label.transactionDescription && !label.transactionName) ||
          !label.transactionLabelAmountKrw
        ) {
          return null;
        }
        const date = dayjs(label.transactionAt).format('M/D');
        const description = label.transactionDescription || label.transactionName || '';
        const amount = label.transactionLabelAmountKrw.toLocaleString();
        return `${date}: ${description} ${amount}원`;
      })
      .filter(Boolean);

    return labels.join('\n');
  };
  return (
    <div
      style={{
        position: 'relative',
      }}
    >
      {props.mappedCount ? (
        (() => {
          const tooltipContent = getTooltipContent();
          const button = (
            <Button
              buttonType="transparent"
              buttonSize="small"
              style={{
                width: '160px',
                overflow: 'hidden',
                textAlign: 'left',
                justifyContent: 'flex-start',
              }}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENT.SETTLEMENT.TRIGGER);
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementListDrawer
                    open={isOpen}
                    onClose={onClose}
                    showEditButton={true}
                    onEditButtonClick={(callback) => {
                      overlays.open(({ isOpen, onClose }) => (
                        <SettlementMappingModal
                          onMappingSuccess={() => {
                            callback?.();
                            onMappingSuccess();
                            onClose();
                          }}
                          onClose={onClose}
                          open={isOpen}
                          onCancel={onClose}
                          taxInvoiceId={props.taxInvoiceId}
                          taxInvoiceType={props.taxInvoiceType}
                          targetDate={props.reportingDate}
                        />
                      ));
                    }}
                    taxInvoiceId={props.taxInvoiceId}
                    type={props.taxInvoiceType}
                    status={props.status}
                  />
                ));
              }}
            >
              <Flex align="center" justify="space-between" style={{ width: '100%' }}>
                <Typography.Text
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    textAlign: 'left',
                  }}
                >
                  {getButtonText()}
                </Typography.Text>
                <RightOutlined style={{ flexShrink: 0, marginLeft: 8 }} />
              </Flex>
            </Button>
          );

          return (
            <Tooltip
              title={<div style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</div>}
              placement="top"
            >
              {button}
            </Tooltip>
          );
        })()
      ) : (
        <PermissionGuard
          required={{
            type: 'taxInvoicePermissions',
          }}
        >
          <Flex align="center" gap={4}>
            <Button
              buttonType="weak"
              buttonSize="small"
              style={{
                width: 'fit-content',
              }}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENT.SETTLEMENT.TRIGGER);
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementMappingModal
                    onMappingSuccess={onMappingSuccess}
                    open={isOpen}
                    onCancel={onClose}
                    onClose={onClose}
                    taxInvoiceId={props.taxInvoiceId}
                    taxInvoiceType={props.taxInvoiceType}
                    targetDate={props.reportingDate}
                  />
                ));
              }}
            >
              은행 거래내역으로 정산
            </Button>
            <Button
              buttonSize="small"
              border
              style={{
                width: 'fit-content',
              }}
              onClick={() => {
                mixpanel.track(MIXPANEL_EVENT.SETTLEMENT.TRIGGER);
                overlays.open(({ isOpen, onClose }) => (
                  <SettlementDirectMappingModal
                    onMappingSuccess={onMappingSuccess}
                    open={isOpen}
                    onCancel={onClose}
                    onClose={onClose}
                    taxInvoiceId={props.taxInvoiceId}
                    taxInvoiceType={props.taxInvoiceType}
                  />
                ));
              }}
            >
              즉시 정산
            </Button>
          </Flex>
        </PermissionGuard>
      )}
    </div>
  );
};

export default MapTransactionToSettlementBtn;
